import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SiteHeader, SiteFooter } from './Layout';
import { AuthContext } from './AuthContext';
import { authFetch } from './auth';
import OcrUploader from './OcrUploader'; // OCR 업로더 컴포넌트 import
import {
  Wallet,
  ChartPie,
  ArrowRightLeft,
  ShieldCheck,
  TrendingUp,
  TrendingDown,
  Coins,
  ArrowRight,
  Printer,
  Calculator,
  Briefcase,
  ChevronDown,
  Pencil,
  X,
  Check,
  CirclePlus
} from 'lucide-react';

const formatNumber = (num) => {
  if (num === undefined || num === null) return '0';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// ── Custom Hook for fetching products ────────────────────────────────
function useProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    setLoading(true);
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backendUrl}/api/products`)
      .then(r => {
        if (!r.ok) throw new Error('상품 데이터를 불러오는 데 실패했습니다.');
        return r.json();
      })
      .then(data => {
        setProducts(Array.isArray(data) ? data : []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const accountProducts = products.filter(p => ['isa', 'pension', 'irp'].includes(p.id));

  return { products, accountProducts, setProducts, loading, error, refresh };
}


// 아이콘/색상은 id별 고정 (JSX는 state에 못 넣으므로 분리)
const PRODUCT_ICONS = {
  isa:     <Wallet className="w-6 h-6" />,
  pension: <TrendingUp className="w-6 h-6" />,
  irp:     <ShieldCheck className="w-6 h-6" />,
};
const PRODUCT_COLORS = {
  isa:     'bg-blue-600',
  pension: 'bg-emerald-600',
  irp:     'bg-indigo-600',
};

// --- 보유 종목 데이터 ---
const generateReturnRates = () => {
  const periods = {
    '1주': 0.5, '2주': 1, '3주': 1.5, '4주': 2, '5주': 2.5,
    '1년': 10,  '2년': 15, '3년': 20, '5년': 30,
  };
  let rates = {};
  for (const [period, baseRate] of Object.entries(periods)) {
    const randomFactor = (Math.random() - 0.5) * 0.5 * baseRate;
    rates[period] = parseFloat((baseRate + randomFactor).toFixed(2));
  }
  return rates;
};

const initialHoldings = {
  isa: [
    { name: '삼성전자',  quantity: 100, value: 28000000  },
    { name: '하이닉스',  quantity: 100, value: 184500000 },
    { name: '하이브',    quantity: 50,  value: 11500000  },
  ],
  pension: [
    { name: 'KODEX 금액티브', quantity: 100, value: 1466000 },
    { name: '1Q 은액티브',    quantity: 100, value: 1100000 },
  ],
  irp: [
    { name: 'KODEX 200 액티브',   quantity: 100, value: 3431500 },
    { name: 'KODEX 미국나스닥100', quantity: 100, value: 2873000 },
    { name: 'KODEX 코스닥150',    quantity: 150, value: 2850000 },
  ],
};

const getInitialHoldingsData = () => {
  const data = JSON.parse(JSON.stringify(initialHoldings));
  for (const account in data) {
    data[account] = data[account].map(item => ({
      ...item,
      expectedReturns: generateReturnRates(),
    }));
  }
  return data;
};

const initialData = getInitialHoldingsData();
const KOSPI_BASE = 3000;

// ─────────────────────────────────────────
// 1. 세액공제 계산기
// ─────────────────────────────────────────
const TaxBenefitCalculator = () => {
  const [income, setIncome] = React.useState(50000000);
  const [pensionDeduction, setPensionDeduction] = React.useState(6000000);
  const [irpDeduction, setIrpDeduction] = React.useState(3000000);

  const totalDeduction = pensionDeduction + irpDeduction;
  const taxRate = income <= 55000000 ? 0.165 : 0.132;
  const expectedTaxBenefit = Math.min(totalDeduction, 9000000) * taxRate;

  return (
    <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-lg">
      <h4 className="text-xl font-bold mb-6 flex items-center gap-3 text-slate-800">
        <Calculator className="text-teal-500" />
        세액공제 계산기 (연금저축 &amp; IRP)
      </h4>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-bold text-slate-600 mb-2">연간 총 급여</label>
          <div className="flex items-center gap-2">
            <input type="range" min="10000000" max="120000000" step="1000000"
              value={income} onChange={(e) => setIncome(Number(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer" />
            <span className="font-bold text-slate-700 text-lg w-32 text-right">{formatNumber(income / 10000)}만원</span>
          </div>
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-600 mb-2">연금저축 납입액 (최대 600만원)</label>
          <div className="flex items-center gap-2">
            <input type="range" min="0" max="6000000" step="100000"
              value={pensionDeduction} onChange={(e) => setPensionDeduction(Number(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer" />
            <span className="font-bold text-slate-700 text-lg w-32 text-right">{formatNumber(pensionDeduction / 10000)}만원</span>
          </div>
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-600 mb-2">IRP 추가 납입액 (최대 900만원, 연금저축 포함)</label>
          <div className="flex items-center gap-2">
            <input type="range" min="0" max={9000000 - pensionDeduction} step="100000"
              value={irpDeduction} onChange={(e) => setIrpDeduction(Number(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer" />
            <span className="font-bold text-slate-700 text-lg w-32 text-right">{formatNumber(irpDeduction / 10000)}만원</span>
          </div>
        </div>
      </div>
      <div className="mt-8 pt-6 border-t border-slate-200 text-center">
        <p className="text-sm text-slate-500">총 공제 대상 금액: {formatNumber(totalDeduction)}원</p>
        <p className="text-lg font-bold text-slate-800 mt-2">예상 최대 세액공제 혜택</p>
        <p className="text-3xl font-black text-teal-600 mt-1">약 {formatNumber(Math.floor(expectedTaxBenefit))}원</p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// 2. 포트폴리오 추천
// ─────────────────────────────────────────
const PortfolioRecommender = () => {
  const [riskLevel, setRiskLevel] = React.useState('medium');
  const recommendations = {
    low:    { name: '안정형', stocks: 20, bonds: 60, cash: 20 },
    medium: { name: '중립형', stocks: 50, bonds: 40, cash: 10 },
    high:   { name: '공격형', stocks: 80, bonds: 15, cash:  5 },
  };
  const current = recommendations[riskLevel];

  return (
    <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-lg">
      <h4 className="text-xl font-bold mb-6 flex items-center gap-3 text-slate-800">
        <ChartPie className="text-purple-500" />
        포트폴리오 추천
      </h4>
      <div className="space-y-4 mb-6">
        <label className="block text-sm font-bold text-slate-600 mb-2">투자 성향</label>
        <div className="flex justify-between gap-2 p-1 bg-slate-100 rounded-xl">
          {Object.keys(recommendations).map(level => (
            <button key={level} onClick={() => setRiskLevel(level)}
              className={`w-full py-2 rounded-lg font-semibold transition-colors ${riskLevel === level ? 'bg-white text-purple-600 shadow' : 'text-slate-500'}`}>
              {recommendations[level].name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-sm font-bold text-slate-600 mb-3">추천 비중 ({current.name})</p>
        <div className="w-full h-8 bg-slate-200 rounded-full flex overflow-hidden">
          <div className="h-full bg-blue-500 flex items-center justify-center text-xs text-white font-bold" style={{ width: `${current.stocks}%` }}>주식 {current.stocks}%</div>
          <div className="h-full bg-sky-500 flex items-center justify-center text-xs text-white font-bold" style={{ width: `${current.bonds}%` }}>채권 {current.bonds}%</div>
          <div className="h-full bg-slate-400 flex items-center justify-center text-xs text-white font-bold" style={{ width: `${current.cash}%` }}>현금 {current.cash}%</div>
        </div>
        <p className="mt-4 text-xs text-slate-500">* 본 추천은 일반적인 투자 성향에 따른 예시이며, 실제 투자 결정은 전문가와 상담이 필요합니다.</p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// 3. 코스피 지수 슬라이더
// ─────────────────────────────────────────
const KospiSlider = ({ kospiIndex, setKospiIndex }) => {
  const changeRate = (((kospiIndex - KOSPI_BASE) / KOSPI_BASE) * 100).toFixed(2);
  const isUp = kospiIndex >= KOSPI_BASE;
  const pct = ((kospiIndex - 1500) / (10000 - 1500)) * 100;

  return (
    <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-lg">
      <h4 className="text-xl font-bold mb-2 flex items-center gap-3 text-slate-800">
        {isUp
          ? <TrendingUp className="text-green-500" />
          : <TrendingDown className="text-red-500" />}
        코스피 지수 연동 시뮬레이터
      </h4>
      <p className="text-sm text-slate-500 mb-6">슬라이더로 코스피 지수를 조정하면 아래 포트폴리오의 기대 수익률이 연동됩니다.</p>

      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1">
          <input
            type="range" min="1500" max="10000" step="10"
            value={kospiIndex}
            onChange={(e) => setKospiIndex(Number(e.target.value))}
            className="w-full h-3 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, ${isUp ? '#22c55e' : '#ef4444'} 0%, ${isUp ? '#22c55e' : '#ef4444'} ${pct}%, #e2e8f0 ${pct}%, #e2e8f0 100%)`,
            }}
          />
          <div className="flex justify-between text-xs text-slate-400 mt-1">
            <span>1,500</span>
            <span>3,000 (기준)</span>
            <span>5,000</span>
            <span>7,500</span>
            <span>10,000</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between bg-slate-50 rounded-2xl p-4">
        <div>
          <p className="text-xs text-slate-500 mb-1">현재 설정 지수</p>
          <p className="text-3xl font-black text-slate-800">{formatNumber(kospiIndex)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-slate-500 mb-1">기준 대비 (3,000)</p>
          <p className={`text-2xl font-black ${isUp ? 'text-green-600' : 'text-red-600'}`}>
            {isUp ? '+' : ''}{changeRate}%
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500 mb-1">수익률 배율</p>
          <p className={`text-2xl font-black ${isUp ? 'text-green-600' : 'text-red-600'}`}>
            ×{(kospiIndex / KOSPI_BASE).toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// 4. 계좌별 포트폴리오 (코스피 연동)
// ─────────────────────────────────────────
const AccountHoldings = ({ kospiMultiplier }) => {
  const [expandedItem, setExpandedItem] = React.useState(null);
  const [holdingsData, setHoldingsData] = React.useState(initialData);
  const [addingAccount, setAddingAccount] = React.useState(null);
  const [newItem, setNewItem] = React.useState({ name: '', quantity: '', value: '' });

  const toggleExpand = (itemName) => {
    setExpandedItem(expandedItem === itemName ? null : itemName);
  };

  const applyMultiplier = (rate) =>
    typeof rate === 'number'
      ? parseFloat((rate * kospiMultiplier).toFixed(2))
      : rate;

  const handleDeleteItem = (account, idx) => {
    setHoldingsData(prev => {
      const updated = prev[account].filter((_, i) => i !== idx);
      if (expandedItem === prev[account][idx]?.name) setExpandedItem(null);
      return { ...prev, [account]: updated };
    });
  };

  const handleAddItem = (account) => {
    if (!newItem.name.trim()) return;
    setHoldingsData(prev => ({
      ...prev,
      [account]: [
        ...prev[account],
        {
          name: newItem.name.trim(),
          quantity: Number(newItem.quantity) || 0,
          value: Number(newItem.value) || 0,
          expectedReturns: generateReturnRates(),
        },
      ],
    }));
    setAddingAccount(null);
    setNewItem({ name: '', quantity: '', value: '' });
  };

  return (
    <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-lg" style={{ color: '#000000' }}>
      <h4 className="text-xl font-bold mb-2 flex items-center gap-3 text-slate-800">
        <Briefcase className="text-indigo-500" />
        계좌별 포트폴리오 (예시)
      </h4>
      {kospiMultiplier !== 1 && (
        <p className="text-sm mb-6 font-medium" style={{ color: kospiMultiplier > 1 ? '#16a34a' : '#dc2626' }}>
          코스피 ×{kospiMultiplier.toFixed(2)} 배율 적용 중 — 수익률이 조정되었습니다.
        </p>
      )}
      {kospiMultiplier === 1 && <p className="text-sm text-slate-500 mb-6">기준 코스피(3,000) 기준 수익률입니다.</p>}

      <div className="space-y-8">
        {Object.keys(holdingsData).map(account => {
          const accountHoldings = holdingsData[account] || [];
          const totalValue = accountHoldings.reduce((sum, item) => sum + item.value, 0);

          const weightedReturnSum = accountHoldings.reduce((sum, item) => {
            const r = applyMultiplier(item.expectedReturns?.['1년']);
            if (typeof r !== 'number') return sum;
            return sum + item.value * (r / 100);
          }, 0);

          const overallExpectedReturn = totalValue > 0 ? (weightedReturnSum / totalValue) * 100 : 0;

          return (
            <div key={account}>
              <div className="flex justify-between items-center mb-3 pb-3 border-b border-slate-200">
                <div>
                  <h5 className="font-bold text-xl text-indigo-700">
                    {account === 'isa' ? '중개형 ISA' : account === 'pension' ? '연금저축' : 'IRP'}
                  </h5>
                  <p className="text-sm text-slate-500">총 평가액: {formatNumber(totalValue)}원</p>
                </div>
                <div className="text-right">
                  <p className={`font-bold text-lg ${overallExpectedReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {overallExpectedReturn.toFixed(2)}%
                  </p>
                  <p className="text-xs text-slate-500">전체 기대 수익률 (1년)</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-slate-500 bg-slate-50">
                    <tr>
                      <th className="p-3 w-12"></th>
                      <th className="p-3">종목명</th>
                      <th className="p-3 text-right">수량</th>
                      <th className="p-3 text-right">평가액</th>
                      <th className="p-3 text-right">1년 기대 수익률</th>
                      <th className="p-3 w-10 no-print"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {accountHoldings.map((item, idx) => {
                      const yearlyReturn = applyMultiplier(item.expectedReturns?.['1년']);
                      return (
                        <React.Fragment key={item.name + idx}>
                          <tr className="border-b border-slate-100 hover:bg-slate-50/50 group">
                            <td className="p-3 text-center">
                              <button onClick={() => toggleExpand(item.name)}
                                className="p-1 rounded-full hover:bg-slate-200 transition-colors">
                                <ChevronDown className={`w-4 h-4 transition-transform ${expandedItem === item.name ? 'rotate-180' : ''}`} />
                              </button>
                            </td>
                            <td className="p-3 font-medium">{item.name}</td>
                            <td className="p-3 text-right">{formatNumber(item.quantity)}</td>
                            <td className="p-3 text-right">{formatNumber(item.value)}원</td>
                            <td className={`p-3 text-right font-bold ${typeof yearlyReturn === 'number' && yearlyReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {typeof yearlyReturn === 'number' ? `${yearlyReturn.toFixed(2)}%` : 'N/A'}
                            </td>
                            <td className="p-3 text-center no-print">
                              <button
                                onClick={() => handleDeleteItem(account, idx)}
                                className="p-1 rounded-full text-slate-300 hover:bg-red-50 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                                title="종목 삭제">
                                <X className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                          {expandedItem === item.name && (
                            <tr className="bg-slate-50">
                              <td colSpan="6" className="p-4">
                                <h6 className="font-bold text-sm mb-2">기간별 상세 기대 수익률</h6>
                                <div className="grid grid-cols-3 sm:grid-cols-5 gap-4 text-xs">
                                  {Object.entries(item.expectedReturns || {}).map(([period, rate]) => {
                                    const adjusted = applyMultiplier(rate);
                                    return (
                                      <div key={period} className="bg-white p-2 rounded-md border border-slate-200 text-center">
                                        <p className="font-bold text-slate-600">{period}</p>
                                        <p className={`font-mono text-sm font-bold ${typeof adjusted === 'number' && adjusted >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {typeof adjusted === 'number' ? `${adjusted}%` : 'N/A'}
                                        </p>
                                      </div>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* 종목 추가 */}
              {addingAccount === account ? (
                <div className="mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                  <p className="text-sm font-bold text-slate-700 mb-3">새 종목 추가</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                    <input
                      type="text"
                      placeholder="종목명"
                      value={newItem.name}
                      onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))}
                      className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                    <input
                      type="number"
                      placeholder="수량"
                      value={newItem.quantity}
                      onChange={e => setNewItem(p => ({ ...p, quantity: e.target.value }))}
                      className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                    <input
                      type="number"
                      placeholder="평가액 (원)"
                      value={newItem.value}
                      onChange={e => setNewItem(p => ({ ...p, value: e.target.value }))}
                      className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAddItem(account)}
                      className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                    >
                      <Check className="w-4 h-4" /> 추가
                    </button>
                    <button
                      onClick={() => { setAddingAccount(null); setNewItem({ name: '', quantity: '', value: '' }); }}
                      className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-bold text-slate-600 bg-slate-200 hover:bg-slate-300 transition-colors"
                    >
                      <X className="w-4 h-4" /> 취소
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setAddingAccount(account)}
                  className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-indigo-600 border border-indigo-200 hover:bg-indigo-50 transition-colors"
                >
                  <CirclePlus className="w-4 h-4" /> 종목 추가
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// 5. 자금 흐름 파이프라인 (연결 전략)
// ─────────────────────────────────────────
const FLOW_META = {
  isa:     { pathTag: '연 2,000만원 한도', outcome: '비과세 최대 400만원', color: '#3b82f6', dot: '#60a5fa', rgb: '59,130,246', glow: 'rgba(59,130,246,0.35)', delay: ['0s', '-0.8s', '-1.6s'],     emoji: '💼' },
  pension: { pathTag: '연 600만원 세액공제', outcome: '세액공제 최대 99만원', color: '#10b981', dot: '#34d399', rgb: '16,185,129', glow: 'rgba(16,185,129,0.35)', delay: ['-0.4s', '-1.2s', '-2s'],   emoji: '📈' },
  irp:     { pathTag: '통합 900만원 공제', outcome: '절세 최대 148만원',    color: '#6366f1', dot: '#a5b4fc', rgb: '99,102,241', glow: 'rgba(99,102,241,0.35)', delay: ['-0.8s', '-1.6s', '-2.4s'], emoji: '🛡️' },
};

const FlowPipeline = ({ products, selectedProduct, setSelectedProduct }) => {
  const [hoveredId, setHoveredId] = React.useState(null);
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── 데스크탑 전용 상수 ──
  const CARD_H = 108;
  const GAP    = 16;
  const COL_H  = 3 * CARD_H + 2 * GAP;
  const SRC_Y  = COL_H / 2;
  const DST_Y  = [CARD_H / 2, CARD_H + GAP + CARD_H / 2, 2 * (CARD_H + GAP) + CARD_H / 2];
  const SVG_W  = 180;
  const makePath = (dy) =>
    `M 0 ${SRC_Y} C ${SVG_W * 0.55} ${SRC_Y} ${SVG_W * 0.45} ${dy} ${SVG_W} ${dy}`;

  // ── 공통 SVG 필터 id ──
  const GLOW_FILTER = 'fp-dot-glow';

  // ── 공통: 소스 카드 ──
  const SourceCard = ({ compact }) => (
    <div style={{ padding: compact ? '14px 12px' : '18px 14px', textAlign: 'center', background: 'rgba(59,130,246,0.1)', border: '2px solid rgba(59,130,246,0.35)', borderRadius: 18, position: 'relative', boxShadow: '0 0 30px rgba(59,130,246,0.18)', width: compact ? '100%' : '100%' }}>
      <div style={{ width: compact ? 38 : 44, height: compact ? 38 : 44, borderRadius: '50%', background: 'rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', boxShadow: '0 0 18px rgba(59,130,246,0.55)' }}>
        <Coins style={{ width: compact ? 18 : 22, height: compact ? 18 : 22, color: '#60a5fa' }} />
      </div>
      <p style={{ fontWeight: 900, color: '#fff', fontSize: compact ? 13 : 14, margin: '0 0 2px' }}>종합 계좌</p>
      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', margin: 0 }}>CMA / 위탁</p>
      <div style={{ position: 'absolute', top: -8, left: -8, right: -8, bottom: -8, border: '1px solid rgba(59,130,246,0.22)', borderRadius: 26, animation: 'fp-pulse 2.5s ease-in-out infinite', pointerEvents: 'none' }} />
    </div>
  );

  // ── 공통: 상품 카드 ──
  const ProductCard = ({ p, mobile }) => {
    const f      = FLOW_META[p.id];
    const dimmed = !mobile && hoveredId && hoveredId !== p.id;
    const active = selectedProduct === p.id;
    return (
      <div
        onClick={() => setSelectedProduct(p.id)}
        onMouseEnter={() => !mobile && setHoveredId(p.id)}
        onMouseLeave={() => !mobile && setHoveredId(null)}
        style={{
          padding: mobile ? '14px 16px' : '12px 16px',
          ...(mobile ? {} : { height: CARD_H, boxSizing: 'border-box' }),
          borderRadius: 16, cursor: 'pointer',
          background: active ? `rgba(${f.rgb},0.18)` : 'rgba(255,255,255,0.04)',
          border: `1px solid ${active ? f.color + 'aa' : 'rgba(255,255,255,0.09)'}`,
          boxShadow: active ? `0 0 24px ${f.glow}` : 'none',
          transition: 'all 0.3s',
          opacity: dimmed ? 0.22 : 1,
          filter: dimmed ? 'grayscale(55%)' : 'none',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: `rgba(${f.rgb},0.22)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: active ? `0 0 12px ${f.glow}` : 'none' }}>
              <span style={{ fontSize: 18 }}>{f.emoji}</span>
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontWeight: 900, color: '#fff', fontSize: 14, margin: 0 }}>{p.name}</p>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.fullName}</p>
            </div>
          </div>
          <p style={{ fontWeight: 800, color: f.dot, fontSize: 11, margin: 0, textAlign: 'right', flexShrink: 0, lineHeight: 1.3 }}>{f.outcome}</p>
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {p.benefits.slice(0, 3).map((b, i) => (
            <span key={i} style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: `rgba(${f.rgb},0.15)`, color: f.dot, border: `1px solid rgba(${f.rgb},0.35)` }}>{b}</span>
          ))}
        </div>
      </div>
    );
  };

  // ── 공통: 아웃컴 섹션 ──
  const OutcomeSection = ({ mobile }) => (
    <div style={{ marginTop: 20, padding: mobile ? '16px 14px' : '20px 22px', borderRadius: 20, background: 'linear-gradient(135deg,rgba(59,130,246,0.08),rgba(99,102,241,0.08),rgba(16,185,129,0.06))',
 border: '1px solid rgba(255,255,255,0.09)' }}>
      <p style={{ color: 'rgba(255,255,255,0.28)', fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 12 }}>Final Outcome — 3총사 시너지</p>
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(1,1fr)' : 'repeat(3,1fr)', gap: mobile ? 8 : 10, marginBottom: 10 }}>
        {[
          { label: '연간 최대 절세', value: '148만원', sub: '세율 16.5% 기준', color: '#60a5fa' },
          { label: 'ISA → 연금 추가공제', value: '+300만원', sub: '만기 자금 연금 전환 시', color: '#34d399' },
          { label: '과세이연 복리 효과', value: '수십 년 누적', sub: '수령 전까지 세금 없이 운용', color: '#a5b4fc' },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', ...(mobile ? { alignItems: 'center', gap: 14 } : { flexDirection: 'column', alignItems: 'center', textAlign: 'center' }), padding: mobile ? '10px 14px' : '14px 10px', borderRadius: 14, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <p style={{ fontWeight: 900, color: item.color, fontSize: mobile ? 22 : 20, margin: 0, flexShrink: 0 }}>{item.value}</p>
            <div>
              <p style={{ fontWeight: 700, color: '#fff', fontSize: 11, margin: mobile ? 0 : '4px 0 2px' }}>{item.label}</p>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.32)', margin: 0 }}>{item.sub}</p>
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: '12px 14px', borderRadius: 14, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>⚡</span>
        <div>
          <p style={{ fontWeight: 800, color: '#fff', fontSize: mobile ? 12 : 13, margin: '0 0 2px' }}>ISA → 연금저축/IRP 전환 시너지</p>
          <p style={{ fontSize: mobile ? 11 : 12, color: 'rgba(255,255,255,0.42)', margin: 0 }}>ISA 만기 자금을 연금계좌로 이전하면 전환금액의 10%(최대 300만원) 추가 세액공제를 받을 수 있습니다</p>
        </div>
      </div>
    </div>
  );

  const CONTAINER = { background: 'linear-gradient(160deg,#060d1f 0%,#0d1530 60%,#060d1f 100%)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 28, overflow: 'hidden' };

  // ═══════════════════════════════════════
  // 모바일 레이아웃: 수직 파이프라인
  // ═══════════════════════════════════════
  if (isMobile) {
    const VC_H = 60; // 수직 커넥터 높이
    return (
      <div style={{ ...CONTAINER, padding: '22px 16px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 20 }}>
          <p style={{ color: 'rgba(255,255,255,0.28)', fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 6 }}>Smart Money Flow</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
            <span style={{ background: 'linear-gradient(135deg,#3b82f6,#6366f1)', borderRadius: 10, padding: '6px 7px', display: 'inline-flex', boxShadow: '0 0 16px rgba(99,102,241,0.45)', flexShrink: 0 }}>
              <ArrowRightLeft style={{ width: 16, height: 16, color: '#fff' }} />
            </span>
            <h3 style={{ color: '#fff', fontWeight: 900, fontSize: 16, margin: 0, lineHeight: 1.3 }}>자금 흐름 &amp; 절세 파이프라인</h3>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.38)', fontSize: 12, margin: 0, lineHeight: 1.5 }}>종합 계좌에서 출발한 자금이 각 절세 계좌를 통과하며 복리로 불어납니다</p>
        </div>

        {/* 소스 카드 */}
        <div style={{ maxWidth: 200, margin: '0 auto' }}>
          <SourceCard compact />
        </div>

        {/* 상품별 수직 커넥터 + 카드 */}
        {products.map((p) => {
          const f = FLOW_META[p.id];
          const pathId = `m-vp-${p.id}`;
          return (
            <React.Fragment key={p.id}>
              {/* 수직 애니메이션 커넥터 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 24px' }}>
                <div style={{ flexShrink: 0 }}>
                  <svg width="32" height={VC_H} style={{ display: 'block', overflow: 'visible' }}>
                    <defs>
                      <linearGradient id={`m-vg-${p.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%"   stopColor={f.color} stopOpacity="0.2" />
                        <stop offset="100%" stopColor={f.color} stopOpacity="0.9" />
                      </linearGradient>
                      <filter id={`m-glow-${p.id}`} x="-100%" y="-100%" width="300%" height="300%">
                        <feGaussianBlur stdDeviation="2" result="blur" />
                        <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                      </filter>
                    </defs>
                    <path id={pathId} d={`M 16 0 L 16 ${VC_H}`} stroke={`url(#m-vg-${p.id})`} strokeWidth="1.5" fill="none" />
                    {[0, 1].map(di => (
                      <circle key={di} r={di === 0 ? 4 : 2.5} fill={f.dot} filter={`url(#m-glow-${p.id})`} opacity={di === 0 ? 1 : 0.5}>
                        <animateMotion dur="1.6s" repeatCount="indefinite" begin={di === 0 ? '0s' : '-0.8s'}>
                          <mpath href={`#${pathId}`} />
                        </animateMotion>
                      </circle>
                    ))}
                  </svg>
                </div>
                {/* 경로 태그 */}
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: `rgba(${f.rgb},0.18)`, border: `1px solid rgba(${f.rgb},0.4)`, color: f.dot, whiteSpace: 'nowrap' }}>
                  {f.pathTag}
                </span>
              </div>

              {/* 상품 카드 */}
              <ProductCard p={p} mobile />
            </React.Fragment>
          );
        })}

        <OutcomeSection mobile />
      </div>
    );
  }

  // ═══════════════════════════════════════
  // 데스크탑 레이아웃: 수평 파이프라인
  // ═══════════════════════════════════════
  return (
    <div style={{ ...CONTAINER, padding: '28px 24px' }}>
      {/* 헤더 */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ color: 'rgba(255,255,255,0.28)', fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 8 }}>Smart Money Flow</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <span style={{ background: 'linear-gradient(135deg,#3b82f6,#6366f1)', borderRadius: 12, padding: '7px 8px', display: 'inline-flex', boxShadow: '0 0 20px rgba(99,102,241,0.45)' }}>
            <ArrowRightLeft style={{ width: 18, height: 18, color: '#fff' }} />
          </span>
          <h3 style={{ color: '#fff', fontWeight: 900, fontSize: 20, margin: 0 }}>자금 흐름 &amp; 절세 파이프라인</h3>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.38)', fontSize: 13, margin: 0 }}>종합 계좌에서 출발한 자금이 각 절세 계좌를 통과하며 세금 없이 복리로 불어납니다</p>
      </div>

      {/* 파이프라인 본문 */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
        {/* 소스 카드 */}
        <div style={{ flexShrink: 0, width: 128, height: COL_H, display: 'flex', alignItems: 'center' }}>
          <SourceCard />
        </div>

        {/* SVG 수평 커넥팅 패스 */}
        <div style={{ position: 'relative', flexShrink: 0, width: SVG_W, height: COL_H }}>
          <svg width={SVG_W} height={COL_H} style={{ display: 'block', overflow: 'visible' }}>
            <defs>
              {products.map(p => {
                const f = FLOW_META[p.id];
                return (
                  <linearGradient key={p.id} id={`fp-grad-${p.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%"   stopColor={f.color} stopOpacity="0.12" />
                    <stop offset="100%" stopColor={f.color} stopOpacity="0.85" />
                  </linearGradient>
                );
              })}
              <filter id={GLOW_FILTER} x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            {products.map((p, idx) => {
              const f      = FLOW_META[p.id];
              const dy     = DST_Y[idx];
              const pathId = `fp-path-${p.id}`;
              const dimmed = hoveredId && hoveredId !== p.id;
              return (
                <g key={p.id} style={{ opacity: dimmed ? 0.12 : 1, transition: 'opacity 0.3s' }}>
                  <path id={pathId} d={makePath(dy)} stroke={`url(#fp-grad-${p.id})`} strokeWidth="1.5" fill="none" />
                  {[0, 1, 2].map(di => (
                    <circle key={di} r={di === 0 ? 4.5 : 2.8} fill={f.dot} filter={`url(#${GLOW_FILTER})`} opacity={di === 0 ? 1 : 0.5}>
                      <animateMotion dur="2.4s" repeatCount="indefinite" begin={f.delay[di]}>
                        <mpath href={`#${pathId}`} />
                      </animateMotion>
                    </circle>
                  ))}
                </g>
              );
            })}
          </svg>
          {/* 경로 위 한도 태그 */}
          {products.map((p, idx) => {
            const f      = FLOW_META[p.id];
            const dy     = DST_Y[idx];
            const labelY = (SRC_Y + dy) / 2 - 9;
            const dimmed = hoveredId && hoveredId !== p.id;
            return (
              <div key={p.id} style={{ position: 'absolute', left: SVG_W * 0.5, top: labelY, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: `rgba(${f.rgb},0.18)`, border: `1px solid rgba(${f.rgb},0.4)`, color: f.dot, pointerEvents: 'none', opacity: dimmed ? 0.12 : 1, transition: 'opacity 0.3s', zIndex: 10 }}>
                {f.pathTag}
              </div>
            );
          })}
        </div>

        {/* 상품 카드 목록 */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: GAP, minWidth: 0 }}>
          {products.map((p) => <ProductCard key={p.id} p={p} mobile={false} />)}
        </div>
      </div>

      <OutcomeSection mobile={false} />
    </div>
  );
};

// ─────────────────────────────────────────
// 메인 Simulator 컴포넌트
// ─────────────────────────────────────────
const Simulator = () => {
  const [activeTab, setActiveTab] = React.useState('summary');
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const printableRef = React.useRef();

  // 코스피 지수 상태
  const [kospiIndex, setKospiIndex] = React.useState(3000);
  const kospiMultiplier = kospiIndex / KOSPI_BASE;

  const { isSuwannews } = React.useContext(AuthContext);

  // 상품 데이터 상태 (편집 가능)
  const { products, accountProducts, setProducts, loading: productsLoading, error: productsError, refresh: refreshProducts } = useProducts();
  const [editingId, setEditingId] = React.useState(null);
  const [editDraft, setEditDraft] = React.useState(null);

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditDraft({ name: p.name, description: p.description, benefits: [...p.benefits] });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const saveEdit = () => {
    const updated = products.map(p => p.id === editingId ? { ...p, ...editDraft } : p);
    setProducts(updated);
    setEditingId(null);
    setEditDraft(null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (isSuwannews && backendUrl) {
      authFetch(`${backendUrl}/api/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      }).catch(() => {});
    }
  };

  const updateBenefit = (idx, value) => {
    setEditDraft(d => {
      const b = [...d.benefits];
      b[idx] = value;
      return { ...d, benefits: b };
    });
  };

  const removeBenefit = (idx) => {
    setEditDraft(d => ({
      ...d,
      benefits: d.benefits.filter((_, i) => i !== idx),
    }));
  };

  const addBenefit = () => {
    setEditDraft(d => ({ ...d, benefits: [...d.benefits, ''] }));
  };

  const handlePrint = () => {
    const printContent = printableRef.current;
    const title = '증권 계좌 상품 비교 분석 리포트';
    const printWindow = window.open('', '', 'height=800,width=800');
    printWindow.document.write('<html><head><title>' + title + '</title>');
    printWindow.document.write('<link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">');
    printWindow.document.write('<style>body{font-family:sans-serif;padding:2rem}@page{size:auto;margin:25mm}.no-print{display:none!important}</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(`<h1 style="font-size:2rem;font-weight:bold;margin-bottom:2rem;text-align:center">${title}</h1>`);
    printWindow.document.write(printContent.innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  const renderVisualMap = () => (
    <FlowPipeline
      products={accountProducts}
      selectedProduct={selectedProduct}
      setSelectedProduct={setSelectedProduct}
    />
  );

  // 상품 카드 — 편집 모드
  const renderEditCard = (p) => (
    <div key={p.id} className="bg-white border-2 border-blue-400 rounded-3xl p-8 flex flex-col h-full shadow-xl">
      <div className="flex items-center gap-3 mb-4">
        <div className={`${PRODUCT_COLORS[p.id]} w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0`}>
          {PRODUCT_ICONS[p.id]}
        </div>
        <input
          value={editDraft.name}
          onChange={(e) => setEditDraft(d => ({ ...d, name: e.target.value }))}
          className="flex-1 text-xl font-black text-slate-800 border-b-2 border-blue-300 focus:border-blue-500 outline-none bg-transparent"
          placeholder="상품명"
        />
      </div>

      <label className="text-xs font-bold text-slate-500 mb-1">설명</label>
      <textarea
        value={editDraft.description}
        onChange={(e) => setEditDraft(d => ({ ...d, description: e.target.value }))}
        rows={3}
        className="w-full text-sm text-slate-700 border border-slate-200 rounded-xl p-3 mb-4 resize-none focus:outline-none focus:border-blue-400"
        placeholder="상품 설명"
      />

      <label className="text-xs font-bold text-slate-500 mb-2">혜택 목록</label>
      <div className="space-y-2 mb-3 flex-grow">
        {editDraft.benefits.map((benefit, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="mt-0.5 bg-green-100 p-0.5 rounded-full flex-shrink-0">
              <CirclePlus className="w-3 h-3 text-green-600" />
            </div>
            <input
              value={benefit}
              onChange={(e) => updateBenefit(i, e.target.value)}
              className="flex-1 text-sm border-b border-slate-200 focus:border-blue-400 outline-none py-0.5"
              style={{ color: '#000000' }}
              placeholder="혜택 내용"
            />
            <button onClick={() => removeBenefit(i)}
              className="text-slate-400 hover:text-red-500 transition-colors flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button onClick={addBenefit}
          className="text-sm text-blue-500 hover:text-blue-700 font-semibold flex items-center gap-1 mt-1">
          <CirclePlus className="w-4 h-4" /> 혜택 추가
        </button>
      </div>

      <div className="flex gap-2 mt-4">
        <button onClick={saveEdit}
          className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
          <Check className="w-4 h-4" /> 저장
        </button>
        <button onClick={cancelEdit}
          className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200 transition-colors flex items-center justify-center gap-2">
          <X className="w-4 h-4" /> 취소
        </button>
      </div>
    </div>
  );

  // 상품 카드 — 보기 모드
  const renderViewCard = (p) => (
    <div key={p.id} className="bg-white border border-slate-200 rounded-3xl p-8 hover:border-blue-300 hover:shadow-xl transition-all group flex flex-col h-full relative">
      {isSuwannews && (
        <button
          onClick={() => startEdit(p)}
          className="absolute top-5 right-5 p-2 rounded-full bg-slate-100 hover:bg-blue-100 hover:text-blue-600 text-slate-400 transition-colors no-print"
          title="편집">
          <Pencil className="w-4 h-4" />
        </button>
      )}
      <div className={`${PRODUCT_COLORS[p.id]} w-14 h-14 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform`}>
        {PRODUCT_ICONS[p.id]}
      </div>
      <h3 className="text-2xl font-black text-slate-800 mb-2">{p.name}</h3>
      <p className="text-slate-500 text-sm mb-6 leading-relaxed flex-grow">{p.description}</p>
      <div className="space-y-3 mb-8">
        {p.benefits.map((benefit, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="mt-1 bg-green-100 p-0.5 rounded-full flex-shrink-0">
              <CirclePlus className="w-3 h-3 text-green-600" />
            </div>
            <span className="text-sm font-medium text-slate-700">{benefit}</span>
          </div>
        ))}
      </div>
      <button
        onClick={() => { setActiveTab('strategy'); setSelectedProduct(p.id); }}
        className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2">
        데이터 상세보기 <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );

  const renderLoading = () => (
    <div className="text-center py-20">
        <p className="text-lg text-gray-400">데이터를 불러오는 중입니다...</p>
    </div>
  );

  const renderError = () => (
    <div className="text-center py-20 bg-red-900/20 rounded-2xl">
        <p className="text-lg text-red-400">오류: {productsError}</p>
    </div>
  );

  return (
    <div className="min-h-screen text-white font-sans" style={{ background: '#060c1a' }}>
      {/* 배경 orb */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div style={{ position:'absolute', top:'-15%', left:'-8%', width:640, height:640, borderRadius:'50%', background:'rgba(59,130,246,0.15)', filter:'blur(120px)' }} />
        <div style={{ position:'absolute', top:'40%', right:'-10%', width:500, height:500, borderRadius:'50%', background:'rgba(99,102,241,0.12)', filter:'blur(100px)' }} />
        <div style={{ position:'absolute', bottom:'5%', left:'30%', width:400, height:400, borderRadius:'50%', background:'rgba(6,182,212,0.08)', filter:'blur(90px)' }} />
      </div>

      <SiteHeader />

      <div className="relative max-w-6xl mx-auto px-4 md:px-8 pt-24 pb-12" style={{ zIndex: 10 }}>
        <header className="mb-12 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold mb-4"
            style={{ background: 'rgba(59,130,246,0.15)', color: '#93c5fd', border: '1px solid rgba(59,130,246,0.3)' }}>
            Smart Financial Planner v2.2
          </span>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4 text-white">
            증권 계좌 상품{' '}
            <span style={{ background: 'linear-gradient(90deg,#60a5fa,#a5b4fc)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>
              비교 및 분석
            </span>
          </h1>
          <p className="max-w-2xl mx-auto text-lg" style={{ color: 'rgba(255,255,255,0.55)' }}>
            최신 금융 데이터를 바탕으로 ISA, 연금저축, IRP를 분석하고, 시뮬레이터를 통해 최적의 투자 전략을 찾아보세요.
          </p>
        </header>

        {/* 편집자 전용 OCR 업로더 + 데이터 관리 링크 */}
        {isSuwannews && (
          <>
            <OcrUploader onUploadComplete={refreshProducts} />
            <div className="flex justify-end -mt-4 mb-8 no-print">
              <Link to="/manage"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 border border-white/15 hover:bg-white/20 transition-colors text-sm font-semibold text-white/80">
                <Pencil className="w-4 h-4" /> 등록 데이터 관리
              </Link>
            </div>
          </>
        )}

        <div className="flex justify-center mb-10 no-print">
          <div className="p-1.5 rounded-2xl flex gap-1" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}>
            {['summary', 'comparison', 'strategy', 'simulator'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2.5 rounded-xl font-bold transition-all`}
                style={activeTab === tab
                  ? { background: 'rgba(255,255,255,0.12)', color: '#93c5fd', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }
                  : { color: 'rgba(255,255,255,0.45)', background: 'transparent' }}>
                {tab === 'summary' ? '종합 요약' : tab === 'comparison' ? '상세 비교' : tab === 'strategy' ? '연결 전략' : '시뮬레이터'}
              </button>
            ))}
          </div>
        </div>

        {productsLoading ? renderLoading() : productsError ? renderError() : (
          <div ref={printableRef} className="space-y-12">
            {activeTab === 'summary' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {accountProducts.map((p) =>
                  editingId === p.id && isSuwannews ? renderEditCard(p) : renderViewCard(p)
                )}
              </div>
            )}

            {activeTab === 'comparison' && (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-200">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="p-6 font-bold text-slate-800">구분</th>
                      {accountProducts.map(p => (
                        <th key={p.id} className="p-6 font-bold text-slate-800">{p.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr>
                      <td className="p-6 text-sm font-bold text-slate-500 bg-slate-50/50">가입 대상</td>
                      {accountProducts.map(p => <td key={p.id} className="p-6 text-sm text-slate-700">{p.target}</td>)}
                    </tr>
                    <tr>
                      <td className="p-6 text-sm font-bold text-slate-500 bg-slate-50/50">납입 한도</td>
                      {accountProducts.map(p => <td key={p.id} className="p-6 text-sm text-slate-700">{p.limits}</td>)}
                    </tr>
                    <tr>
                      <td className="p-6 text-sm font-bold text-slate-500 bg-slate-50/50">핵심 혜택</td>
                      {accountProducts.map(p => (
                        <td key={p.id} className="p-6">
                          <ul className="text-sm text-slate-700 space-y-1 list-disc list-inside">
                            {p.benefits.map((b, i) => <li key={i}>{b}</li>)}
                          </ul>
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-6 text-sm font-bold text-slate-500 bg-slate-50/50">중도 인출</td>
                      <td className="p-6 text-sm text-slate-700">원금 한도 내 자유로움</td>
                      <td className="p-6 text-sm text-slate-700">조건부 가능 (패널티)</td>
                      <td className="p-6 text-sm text-slate-700">법정 사유 외 제한</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'strategy' && (
              <div className="space-y-8">{renderVisualMap()}</div>
            )}

            {activeTab === 'simulator' && (
              <div className="space-y-8">
                <KospiSlider kospiIndex={kospiIndex} setKospiIndex={setKospiIndex} />
                <TaxBenefitCalculator />
                <PortfolioRecommender />
                <AccountHoldings kospiMultiplier={kospiMultiplier} />
              </div>
            )}
          </div>
        )}

        <div className="mt-12 flex justify-center no-print">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-6 py-3 font-bold rounded-xl transition-all"
            style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.14)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}>
            <Printer className="w-5 h-5" />
            리포트 PDF로 출력하기
          </button>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default Simulator;
